import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EndpointTester = ({ addLog }) => {
  const [testCredentials, setTestCredentials] = useState({
    email: '',
    password: ''
  });
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Test sign-in endpoint
  const testSignIn = async () => {
    if (!testCredentials?.email || !testCredentials?.password) {
      addLog('warn', 'Email and password required for testing');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    addLog('info', `Testing sign-in endpoint for ${testCredentials?.email}...`);

    const startTime = Date.now();
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: testCredentials?.email,
        password: testCredentials?.password
      });

      const responseTime = Date.now() - startTime;

      if (error) {
        setTestResult({
          success: false,
          error: error?.message,
          responseTime,
          timestamp: new Date()?.toISOString()
        });
        addLog('error', 'Sign-in test failed', {
          email: testCredentials?.email,
          error: error?.message,
          responseTime
        });
      } else {
        setTestResult({
          success: true,
          userId: data?.user?.id,
          email: data?.user?.email,
          responseTime,
          timestamp: new Date()?.toISOString()
        });
        addLog('success', 'Sign-in test successful', {
          userId: data?.user?.id,
          email: data?.user?.email,
          responseTime
        });

        // Sign out immediately after test
        await supabase?.auth?.signOut();
        addLog('info', 'Test session signed out');
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      setTestResult({
        success: false,
        error: error?.message || 'Unknown error',
        responseTime,
        timestamp: new Date()?.toISOString()
      });
      addLog('error', 'Sign-in test error', {
        error: error?.message,
        responseTime
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Test password reset endpoint
  const testPasswordReset = async () => {
    setTestLoading(true);
    setTestResult(null);
    addLog('info', `Testing password reset endpoint for ${testCredentials?.email || 'test@mybodytoday.com'}...`);
    
    const startTime = Date.now();
    
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(
        testCredentials?.email || 'test@mybodytoday.com',
        {
          redirectTo: `${window.location?.origin}/auth/confirm?next=/reset-password`
        }
      );

      const responseTime = Date.now() - startTime;

      if (error) {
        setTestResult({
          success: false,
          error: error?.message,
          responseTime,
          timestamp: new Date()?.toISOString()
        });
        addLog('error', 'Password reset test failed', {
          email: testCredentials?.email || 'test@mybodytoday.com',
          error: error?.message,
          responseTime
        });
      } else {
        setTestResult({
          success: true,
          message: 'Password reset email sent',
          responseTime,
          timestamp: new Date()?.toISOString()
        });
        addLog('success', 'Password reset email sent', {
          email: testCredentials?.email || 'test@mybodytoday.com',
          responseTime
        });
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      setTestResult({
        success: false,
        error: error?.message || 'Unknown error',
        responseTime,
        timestamp: new Date()?.toISOString()
      });
      addLog('error', 'Password reset test error', {
        error: error?.message,
        responseTime
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Load demo credentials
  const loadDemoCredentials = (email, password) => {
    setTestCredentials({ email, password });
    addLog('info', `Loaded credentials for ${email}`);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
        <Icon name="TestTube2" size={20} />
        Authentication Endpoint Tester
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Test Email
            </label>
            <Input
              type="email"
              value={testCredentials?.email}
              onChange={(e) => setTestCredentials(prev => ({ ...prev, email: e?.target?.value }))}
              placeholder="Enter email to test"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Test Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={testCredentials?.password}
                onChange={(e) => setTestCredentials(prev => ({ ...prev, password: e?.target?.value }))}
                placeholder="Enter password to test"
                className="w-full pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>
          </div>

          {/* Quick Load Demo Credentials */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Quick Load Demo Credentials
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDemoCredentials('child.demo@mybodytoday.com', 'ChildDemo123!')}
              >
                Child Demo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDemoCredentials('elderly.demo@mybodytoday.com', 'ElderlyDemo123!')}
              >
                Elderly Demo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDemoCredentials('condition.demo@mybodytoday.com', 'ConditionDemo123!')}
              >
                Condition Demo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDemoCredentials('adult.demo@mybodytoday.com', 'AdultDemo123!')}
              >
                Adult Demo
              </Button>
            </div>
          </div>

          {/* Test Actions */}
          <div className="flex gap-2">
            <Button
              onClick={testSignIn}
              disabled={testLoading || !testCredentials?.email || !testCredentials?.password}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {testLoading ? (
                <Icon name="Loader2" size={16} className="animate-spin" />
              ) : (
                <Icon name="LogIn" size={16} />
              )}
              Test Sign-In
            </Button>
            <Button
              variant="outline"
              onClick={testPasswordReset}
              disabled={testLoading || !testCredentials?.email}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {testLoading ? (
                <Icon name="Loader2" size={16} className="animate-spin" />
              ) : (
                <Icon name="KeyRound" size={16} />
              )}
              Test Reset
            </Button>
          </div>
        </div>

        {/* Result Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Test Result
          </label>
          {testResult ? (
            <div className={`p-4 rounded-lg border ${
              testResult?.success
                ? 'bg-green-500/10 border-green-500/20' :'bg-red-500/10 border-red-500/20'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon
                  name={testResult?.success ? 'CheckCircle2' : 'XCircle'}
                  size={20}
                  className={testResult?.success ? 'text-green-500' : 'text-red-500'}
                />
                <span className={`font-medium ${
                  testResult?.success ? 'text-green-500' : 'text-red-500'
                }`}>
                  {testResult?.success ? 'Success' : 'Failed'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                {testResult?.success ? (
                  <>
                    <div>
                      <span className="text-muted-foreground">User ID:</span>
                      <code className="ml-2 text-xs bg-muted px-2 py-0.5 rounded font-mono">
                        {testResult?.userId}
                      </code>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <code className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">
                        {testResult?.email}
                      </code>
                    </div>
                  </>
                ) : (
                  <div>
                    <span className="text-muted-foreground">Error:</span>
                    <code className="ml-2 text-xs bg-muted px-2 py-0.5 rounded text-red-500">
                      {testResult?.error}
                    </code>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Response Time:</span>
                  <span className="ml-2 text-xs">{testResult?.responseTime}ms</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Timestamp:</span>
                  <span className="ml-2 text-xs">
                    {new Date(testResult.timestamp)?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-dashed border-border rounded-lg text-muted-foreground">
              <div className="text-center">
                <Icon name="TestTube2" size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No test results yet</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EndpointTester;